/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';

import { agreementQuery } from '@/api/agreements';
import { type Me, signOut } from '@/api/auth';
import { usePreferences } from '@/api/preferences';
import { AmidaGlyph, AmidaMark, VellumQuill } from '@/components/Brand';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import type { IconName } from '@/components/ui/icons';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { type RailSection, railSection } from '@/lib/railSection';
import { roleName } from '@/lib/roles';
import { organizationHome } from '@/lib/settingsNav';

/**
 * The left rail.
 *
 * Four groups, in the order the work happens: look at what needs attention,
 * work on agreements, look something up, and — pinned away from the day's work
 * — settings and help.
 *
 * No item carries a number: how much is waiting is the dashboard's to say.
 */
interface Item {
  to: string;
  /** The section that lights this item: see `railSection`. */
  section: RailSection;
  /** Other sections that light it too. */
  also?: RailSection[];
  label: string;
  icon: IconName;
  needs?: string;
}

interface Group {
  title: string;
  items: Item[];
}

const GROUPS: Group[] = [
  {
    title: 'Overview',
    items: [
      { to: '/dashboard', section: 'dashboard', label: 'Dashboard', icon: 'chart' },
    ],
  },
  {
    title: 'Agreements',
    items: [
      {
        to: '/agreements',
        section: 'agreements',
        // Completed agreements are its second tab.
        also: ['records'],
        label: 'Agreements',
        icon: 'doc',
      },
      {
        to: '/import',
        section: 'import',
        label: 'Import',
        icon: 'down2',
        needs: 'import:run',
      },
    ],
  },
  {
    title: 'Directory',
    items: [
      {
        to: '/counterparties',
        section: 'counterparties',
        label: 'Counterparties',
        icon: 'users',
      },
      {
        to: '/deadlines',
        section: 'deadlines',
        label: 'Deadlines',
        icon: 'cal',
      },
    ],
  },
  {
    title: 'Admin',
    items: [
      // Agreement and packet templates, as two tabs of one page.
      {
        to: '/workflows',
        section: 'workflows',
        also: ['packets'],
        label: 'Templates',
        icon: 'grid',
      },
    ],
  },
];

/** The agreement a path opens, if it opens one. */
const AGREEMENT_PAGE = /^\/agreements\/([^/]+)$/;

/**
 * The stage of the agreement the page open shows, if it shows one.
 *
 * Read from what the page itself asked the server for, and never asked
 * again here: the rail watches that answer in the cache (`enabled: false`)
 * and learns the stage the moment the page does, including when a signature
 * lands while it is open.
 */
function useOpenAgreementStage(path: string): string | undefined {
  const id = AGREEMENT_PAGE.exec(path)?.[1] ?? '';
  const { data } = useQuery({ ...agreementQuery(id), enabled: false });
  return id !== '' ? data?.stage : undefined;
}

/** Where the person is, as the rail says it: one rule for every route. */
function useRailSection(): RailSection | null {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const packet = useRouterState({
    select: (s) => {
      const value: unknown = (s.location.search as Record<string, unknown>)['packet'];
      return typeof value === 'string' ? value : undefined;
    },
  });
  const agreementStage = useOpenAgreementStage(path);
  return railSection(path, { agreementStage, packet });
}

export function Rail({
  permissions,
  me,
}: {
  permissions: Set<string> | undefined;
  me: Me | undefined;
}) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { prefs, set } = usePreferences();
  const section = useRailSection();

  const mini = prefs.rail === 'mini';

  // A sign-out that fails says so, here, rather than leaving somebody who
  // meant to leave sitting signed in without knowing it. The cache is cleared
  // either way (`signOut`), so what they see is reloaded, not left over.
  const out = useMutation({
    mutationFn: () => signOut(client),
    // Unless the browser is already on its way to the identity provider's
    // sign-out page, which sends it back to the sign-in screen itself.
    onSuccess: (leftForProvider) =>
      leftForProvider ? undefined : navigate({ to: '/signin' }),
  });

  const visible = GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.needs || permissions?.has(item.needs)),
  })).filter((group) => group.items.length > 0);

  return (
    <aside className="rail">
      <div className="rail-top">
        <Link to="/dashboard" className="mark" aria-label="Vellum home">
          <AmidaMark />
          <AmidaGlyph />
          <span className="mark-div" />
          <VellumQuill />
          <b>Vellum</b>
        </Link>
        <button
          type="button"
          className="rail-toggle"
          onClick={() => {
            set({ rail: mini ? 'full' : 'mini' });
          }}
          aria-expanded={!mini}
          aria-controls="railnav"
          aria-label={mini ? 'Expand the sidebar' : 'Collapse the sidebar'}
        >
          <Icon name="panel" size={16} />
        </button>
      </div>

      <div className="rail-scroll">
        {/* Where a screen hangs its own context — a workflow's properties, say.
            Rendered here rather than passed down, so no page has to know the
            rail exists beyond portalling into this one element. */}
        <nav className="rail-nav" id="railnav" aria-label="Primary">
          {/* A group's name labels its links; it is not a heading, so the
              page's own h1 stays the first heading in its outline. */}
          {visible.map((group, index) => (
            <div
              className="rail-group"
              key={group.title}
              role="group"
              aria-labelledby={`rail-group-${String(index)}`}
            >
              <p className="rail-label" id={`rail-group-${String(index)}`}>
                {group.title}
              </p>
              {group.items.map((item) => {
                // One rule for every route (`railSection`): starting an
                // agreement lights Agreements though its URL is under
                // /workflows, and a finished agreement lights Records though
                // its URL is under /agreements.
                const on =
                  section !== null &&
                  (section === item.section || (item.also?.includes(section) ?? false));
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={on ? 'on' : ''}
                    title={item.label}
                    // The router marks a link current on its own for any path
                    // beneath it; `on` decides here, so it is told only an
                    // exact match -- where the two agree.
                    activeOptions={{ exact: true }}
                    {...(on ? { 'aria-current': 'page' as const } : {})}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="side" id="rail-side" />
      </div>

      <Menu
        label={`Account menu for ${me?.display_name ?? 'you'}`}
        align="start"
        trigger={(props) => (
          <button
            type="button"
            className={
              section === 'settings' || section === 'help' ? 'rail-user on' : 'rail-user'
            }
            {...props}
            aria-label={`Account menu for ${me?.display_name ?? 'you'}`}
            title={me?.display_name ?? undefined}
          >
            <Avatar initials={me?.initials ?? '··'} size="lg" />
            <span className="ux">
              <span className="un">{me?.display_name ?? 'Signing in'}</span>
              <span className="ur">{roleLine(me)}</span>
            </span>
            <Icon name="kebab" size={15} />
          </button>
        )}
      >
        {(close) => (
          <>
            {/* Settings: People for an Admin, Integrations otherwise, with your
                profile in the same menu beside them. It is the one way in, so
                no two entries open the same pages. What you are told about is
                under the bell. */}
            <MenuItem
              icon={<Icon name="gear" size={15} />}
              title="Settings"
              onClick={() => {
                close();
                void navigate({ to: organizationHome(permissions) });
              }}
            />
            {/* How Vellum works, for everybody: a page, not a toast. */}
            <MenuItem
              icon={<Icon name="info" size={15} />}
              title="Help and documentation"
              onClick={() => {
                close();
                void navigate({ to: '/help' });
              }}
            />
            <hr />
            <MenuItem
              icon={<Icon name="out" size={15} />}
              title="Sign out"
              onClick={() => {
                close();
                out.mutate();
              }}
            />
          </>
        )}
      </Menu>

      {out.isError ? (
        <p className="warn-line rail-err" role="alert">
          <Icon name="warn" size={15} />
          <span>Sign-out did not complete. Try again.</span>
        </p>
      ) : null}

      <div className="rail-foot">
        <span>Powered by</span>
        <AmidaMark className="rail-foot-logo" />
      </div>
    </aside>
  );
}

/** The person's role, as a line under their name. */
function roleLine(me: Me | undefined): string {
  if (!me) return '';
  if (me.roles.length === 0) return 'No role yet';
  return me.roles.map(roleName).join(', ');
}
